import { apiClient } from "./api";

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Training job interface
 */
export interface TrainingJob {
  id: string;
  serviceName: string;
  metricName: string;
  ensembleMethod: string;
  epochs: number;
  trainingDataPoints: number;
  status: string;
  accuracy?: number;
  createdAt: string;
  completedAt?: string;
  [key: string]: any;
}

/**
 * ML Model interface
 */
export interface MLModel {
  id: string;
  name: string;
  serviceName: string;
  metricName: string;
  type: string;
  status: string;
  accuracy?: number;
  version?: string;
  trainedAt?: string;
  [key: string]: any;
}

/**
 * Model version interface
 */
export interface ModelVersion {
  id: string;
  modelId: string;
  version: string;
  accuracy: number;
  trainedAt: string;
  status: string;
  [key: string]: any;
}

/**
 * Detection pipeline interface
 */
export interface DetectionPipeline {
  id: string;
  name: string;
  modelVersionId?: string;
  isActive: boolean;
  serviceName: string;
  metricName: string;
  lastRun?: string;
  [key: string]: any;
}

/**
 * Remediation action interface
 */
export interface RemediationAction {
  id: string;
  name: string;
  description: string;
  type: string;
  script?: string;
  [key: string]: any;
}

/**
 * Remediation rule interface
 */
export interface RemediationRule {
  id: string;
  name: string;
  description: string;
  actionId: string;
  isActive: boolean;
  conditions?: any;
  [key: string]: any;
}

/**
 * Remediation execution interface
 */
export interface RemediationExecution {
  id: string;
  ruleId: string;
  anomalyId: string;
  status: string;
  result?: string;
  executedAt: string;
  [key: string]: any;
}

// ============================================================================
// Model Studio APIs
// ============================================================================

/**
 * Create a new training job
 */
export const createTrainingJob = async (
  serviceName: string,
  metricName: string,
  ensembleMethod: string = "lscp",
  epochs: number = 50,
  trainingDataPoints: number = 200
): Promise<TrainingJob> => {
  const response = await apiClient.post<TrainingJob>("/model-studio/training-jobs", null, {
    params: {
      service_name: serviceName,
      metric_name: metricName,
      ensemble_method: ensembleMethod,
      epochs,
      training_data_points: trainingDataPoints,
    },
  });
  return response.data;
};

/**
 * Get all training jobs
 */
export const getTrainingJobs = async (): Promise<TrainingJob[]> => {
  const response = await apiClient.get<TrainingJob[]>("/model-studio/training-jobs");
  return response.data;
};

/**
 * Get training job by ID
 */
export const getTrainingJob = async (jobId: string): Promise<TrainingJob> => {
  const response = await apiClient.get<TrainingJob>(`/model-studio/training-jobs/${jobId}`);
  return response.data;
};

/**
 * Get all models
 */
export const getAllModels = async (): Promise<MLModel[]> => {
  const response = await apiClient.get<MLModel[]>("/model-studio/models");
  return response.data;
};

/**
 * Get model versions
 */
export const getModelVersions = async (modelId: string): Promise<ModelVersion[]> => {
  const response = await apiClient.get<ModelVersion[]>(`/model-studio/models/${modelId}`);
  return response.data;
};

/**
 * Deploy a model version
 */
export const deployModel = async (
  versionId: string,
  pipelineName: string
): Promise<DetectionPipeline> => {
  const response = await apiClient.post<DetectionPipeline>("/model-studio/deploy", null, {
    params: { version_id: versionId, pipeline_name: pipelineName },
  });
  return response.data;
};

/**
 * Get all pipelines
 */
export const getPipelines = async (): Promise<DetectionPipeline[]> => {
  const response = await apiClient.get<DetectionPipeline[]>("/model-studio/pipelines");
  return response.data;
};

/**
 * Update pipeline status
 */
export const updatePipelineStatus = async (
  pipelineId: string,
  isActive: boolean
): Promise<DetectionPipeline> => {
  const response = await apiClient.put<DetectionPipeline>(
    `/model-studio/pipelines/${pipelineId}/status`,
    null,
    {
      params: { is_active: isActive },
    }
  );
  return response.data;
};

// ============================================================================
// Remediation APIs
// ============================================================================

/**
 * Get all remediation actions
 */
export const getRemediationActions = async (): Promise<RemediationAction[]> => {
  const response = await apiClient.get<RemediationAction[]>("/remediation/actions");
  return response.data;
};

/**
 * Create a remediation action
 */
export const createRemediationAction = async (
  action: Partial<RemediationAction>
): Promise<RemediationAction> => {
  const response = await apiClient.post<RemediationAction>("/remediation/actions", action);
  return response.data;
};

/**
 * Get all remediation rules
 */
export const getRemediationRules = async (): Promise<RemediationRule[]> => {
  const response = await apiClient.get<RemediationRule[]>("/remediation/rules");
  return response.data;
};

/**
 * Create a remediation rule
 */
export const createRemediationRule = async (
  rule: Partial<RemediationRule>
): Promise<RemediationRule> => {
  const response = await apiClient.post<RemediationRule>("/remediation/rules", rule);
  return response.data;
};

/**
 * Update rule status
 */
export const updateRuleStatus = async (
  ruleId: string,
  isActive: boolean
): Promise<RemediationRule> => {
  const response = await apiClient.put<RemediationRule>(
    `/remediation/rules/${ruleId}/status`,
    null,
    {
      params: { is_active: isActive },
    }
  );
  return response.data;
};

/**
 * Get remediation executions
 */
export const getRemediationExecutions = async (): Promise<RemediationExecution[]> => {
  const response = await apiClient.get<RemediationExecution[]>("/remediation/executions");
  return response.data;
};

/**
 * Execute remediation
 */
export const executeRemediation = async (anomalyId: string): Promise<RemediationExecution> => {
  const response = await apiClient.post<RemediationExecution>("/remediation/execute", null, {
    params: { anomaly_id: anomalyId },
  });
  return response.data;
};
